import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendCompany,
	RosterUserInfo,
} from "node-library/dist/portal";
import {
	OrderTableSortColumn,
	SortOrder,
	UserTableSortColumn,
} from "node-library/dist/paginations";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { Severity, UserStatus } from "node-library/dist/constants";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import {
	getUserSortString,
	getUserStatusString,
} from "../../../helpers/userHelper";
import {
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import RosterUserContent from "./rosterUserContent";
import UserDetailsContent from "./userDetailsContent";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import { BlueButton } from "../../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import NewRosterUserModal from "../../../common-components/modals/newRosterUserModal";
import EditRosterUserModal from "../../../common-components/modals/editRosterUserModal";

interface Props {
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
	setUser: React.Dispatch<React.SetStateAction<FrontendUserInfo | undefined>>;
	frontendUser: FrontendUserInfo | undefined;
}

export default function RosterPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [users, setUsers] = useState<Array<RosterUserInfo>>([]);

	const [selectedUser, setSelectedUser] = useState<RosterUserInfo>();

	const [loading, setLoading] = useState<boolean>(false);
	const [adminInAction, setAdminInAction] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<UserTableSortColumn>(
		UserTableSortColumn.INSERTED
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [statusFilterOption, setStatusFilterOption] = useState<UserStatus | "">(
		""
	);
	const [newRosterUserModalOpen, setNewRosterUserModalOpen] =
		useState<boolean>(false);

	const [editRosterUserModalOpen, setEditRosterUserModalOpen] =
		useState<boolean>(false);
	//confirm modals
	const [deleteUserConfirmOpen, setDeleteUserConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getUsers(searchText: string) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/users/get-users", {
				searchText,
				sortBy,
				sortOrder,
				pageNumber: currentPagination,
				statusFilterOption,
			});

			const users: Array<RosterUserInfo> = response?.data?.users;
			const pageCount: number = response?.data?.totalPages;
			setUsers(users);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function resendValidationEmail() {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest("/users/resend-validation-email", {
				userId: (selectedUser as RosterUserInfo).userId,
			});

			openSnackbar(
				translation("ValidationEmailSent"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			goBackToViewUsersPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	const statusSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(UserStatus).map((status: UserStatus) => {
		const name: string = getUserStatusString(status, translation);
		return {
			key: status,
			name: name,
			description: "",
			value: status,
			children: (
				<div
					className={`center-text status-selection-radio-option typography-body-02 ${
						statusFilterOption === status
							? "status-selection-radio-option-selected"
							: ""
					}`}
				>
					{name}
				</div>
			),
		};
	});
	statusSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text status-selection-radio-option ${
					statusFilterOption === ""
						? "status-selection-radio-option-selected"
						: ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});
	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "700px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={statusSelectionOptions}
				selectedOption={statusFilterOption}
				onChange={(e) => {
					setStatusFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const addRosterUserButton: React.ReactNode = (
		<BlueButton
			key={`add-roster-user`}
			id={"add-roster-user-button"}
			onClick={() => {
				setNewRosterUserModalOpen(true);
			}}
			disabled={adminInAction}
			buttonClassName="button-with-left-icon"
		>
			<AddIcon />
			{translation("User")}
		</BlueButton>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(UserTableSortColumn).map((value) => {
					return {
						value: value,
						title: getUserSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
				backgroundColor="transparent"
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getUsers("");
	}, [currentPagination, sortBy, sortOrder, statusFilterOption]);

	function gotoUserDetailsPage(user: RosterUserInfo) {
		setSelectedUser(user);
		setCurrentPageIndex(1);
	}
	function goBackToViewUsersPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedUser(undefined);
		if (reload) {
			getUsers(searchText);
		}
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						minHeight: "calc(100vh - 140px)",
						marginTop: "60px",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<div className="view-orders-page-header">
							<div className="typography-heading-01">
								{translation("Roster")}
							</div>
							<CustomSearchBar
								id="view-order-page-searchbar"
								searchText={searchText}
								setSearchText={setSearchText}
								placeholder={translation("SearchByUserNameorCompanyName")}
								searchButtonText={translation("Search")}
								onSearch={getUsers}
							></CustomSearchBar>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{statusSelection}

							<div
								style={{
									display: "flex",
									gap: "5px",
								}}
							>
								{addRosterUserButton}
								{sortOptionSelection}
							</div>
						</div>

						{loading ? (
							<ViewOrderPageSkeletonUI />
						) : (
							<RosterUserContent
								users={users}
								goToViewUserDetails={gotoUserDetailsPage}
								setDeleteUserConfirmOpen={setDeleteUserConfirmOpen}
								setProceedAction={setProceedAction}
							/>
						)}
					</div>
					{!loading && users.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_900}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<UserDetailsContent
					user={selectedUser as RosterUserInfo}
					goBackToViewUsersPage={goBackToViewUsersPage}
					resendValidationEmail={resendValidationEmail}
					adminInAction={adminInAction}
					frontendUser={props.frontendUser}
					// setDeleteUserConfirmOpen={setDeleteUserConfirmOpen}
					// setProceedAction={setProceedAction}
					editRosterUserModalOpen={editRosterUserModalOpen}
					setEditRosterUserModalOpen={setEditRosterUserModalOpen}
					proceedAction={proceedAction}
					setUnsavedChanges={setUnsavedChanges}
					goBackToViewUserDetailPage={gotoUserDetailsPage}
					setUser={props.setUser}
				></UserDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "16px 24px 16px" }}>
			{currentComponent}

			{/* <DiscardOrderConfirmModal
				open={discardOrderConfirmOpen}
				setOpen={setDiscardOrderConfirmOpen}
				proceedAction={proceedAction}
			></DiscardOrderConfirmModal> */}

			{
				<NewRosterUserModal
					open={newRosterUserModalOpen}
					setOpen={setNewRosterUserModalOpen}
					proceedAction={proceedAction}
					setUnsavedChanges={setUnsavedChanges}
					goBackToViewUsersPage={goBackToViewUsersPage}
				></NewRosterUserModal>
			}
		</div>
	);
}
